import { useCallback, useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, badge } from '../../utils/uiStyles';
import { kstToday } from '../../utils/kstTime';

/* 센터 실적 «기준 데이터 → 날짜별 가중치» — 월 목표 진척도(누적 가중치 ÷ 월 전체 가중치)에 쓰는 날짜별 계수.
 * · 하루 가중치 = 요일지수(비면 요일 기본값) × 주차지수 × 휴일고려 × 휴일계수 × 기타계수(빈 칸 = 1). 계산은 서버가 하고
 *   화면은 응답(/center-perf/weights?month=YYYY-MM)의 하루 가중치·누적 진척도를 그대로 그린다.
 * · 달력(월~일 7열) 칸을 고르면 아래에 그 날 편집 줄이 열린다. canManage(운영·시스템 관리자·기준 데이터 관리 조직)만 입력할 수 있고
 *   «가중치 저장»은 **바뀐 날만** PUT 으로 보낸다(다섯 칸을 모두 비우면 서버가 그 날 행을 지운다). 값이 하나라도 틀리면 서버가 400 이고
 *   아무것도 저장하지 않는다 — 그 문구를 그대로 보인다. 권한은 서버가 다시 판정한다.
 * · 그 달 행이 하나도 없으면 «미입력» 배너 — 요일 기본값만으로 계산한다. BPO 가져오기의 요일지수(연도) 시트로도 채운다.
 */
export const FACTORS = [['dow_index', '요일지수'], ['week_index', '주차지수'], ['holiday_consider', '휴일고려'],
  ['holiday_coef', '휴일계수'], ['etc_coef', '기타계수']];
export const DOW = ['월', '화', '수', '목', '금', '토', '일'];
export const MISSING_TEXT = '이번 달 휴일·주차 계수 미입력 — 요일 기본값만으로 계산합니다';

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const inp = { padding: '4px 6px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '6px 12px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(84px, 1fr))', gap: '4px' };
const MONTH_RE = /^\d{4}-\d{2}$/;

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};

// 누적 진척도(0~1 문자열) → '50.1%'(소수 1자리).
export const pctText = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';
};

const draftOf = (day) => Object.fromEntries(FACTORS.map(([k]) => [k, day[k] === null || day[k] === undefined ? '' : String(day[k])]));

// 바뀐 날만 PUT 본문으로 — 빈칸 = null. 숫자 판정은 서버 몫이라 문자열 그대로 보낸다.
export function changedDays(days, drafts) {
  const out = [];
  (days || []).forEach((day) => {
    const draft = drafts[day.date];
    if (!draft) return;
    const base = draftOf(day);
    if (FACTORS.every(([k]) => String(draft[k] ?? '').trim() === base[k])) return;
    const entry = { date: day.date };
    FACTORS.forEach(([k]) => {
      const t = String(draft[k] ?? '').trim();
      entry[k] = t === '' ? null : t;
    });
    out.push(entry);
  });
  return out;
}

export default function WeightsSection({ canManage = false }) {
  const [month, setMonth] = useState(() => (kstToday() || '').slice(0, 7));
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState('');
  const [dowDraft, setDowDraft] = useState([]);
  const [msg, setMsg] = useState(null);               // { tone: 'ok' | 'err', text }
  const [busy, setBusy] = useState(false);

  // 응답을 그린다 — 고치던 값은 서버 값으로 되돌린다.
  const show = useCallback((payload) => {
    setData(payload);
    setDrafts({});
    setDowDraft((payload?.dow_defaults || []).map((v) => String(v)));
  }, []);

  useEffect(() => {
    if (!MONTH_RE.test(month)) return undefined;
    let alive = true;
    setLoadError('');
    setSelected('');
    setMsg(null);
    centerPerfAPI.weights(month)
      .then((r) => { if (alive) show(r.data || null); })
      .catch((e) => {
        if (!alive) return;
        setData(null);
        setLoadError(detailOf(e, '날짜별 가중치를 불러오지 못했습니다.'));
      });
    return () => { alive = false; };
  }, [month, show]);

  const days = data?.days || [];
  const changes = changedDays(days, drafts);
  const dowDefaults = (data?.dow_defaults || []).map((v) => String(v));
  const dowChanged = dowDraft.length === 7 && dowDraft.some((v, i) => v.trim() !== dowDefaults[i]);
  const selectedDay = days.find((d) => d.date === selected) || null;
  const selectedDraft = selectedDay ? (drafts[selectedDay.date] || draftOf(selectedDay)) : null;

  const run = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      setMsg({ tone: 'ok', text: typeof okText === 'function' ? okText(r?.data || {}) : okText });
      return true;
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '저장하지 못했습니다.') });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (day, key, value) => setDrafts((prev) => ({
    ...prev, [day.date]: { ...(prev[day.date] || draftOf(day)), [key]: value },
  }));

  const save = () => {
    if (!changes.length || busy) return;
    const body = changes;
    run(async () => {
      const r = await centerPerfAPI.saveWeights(month, body);
      show(r.data || null);
      return r;
    }, `${body.length}일 가중치를 저장했습니다`);
  };

  const clear = async () => {
    if (busy) return;
    const ok = await confirmDialog(`${month} 의 날짜별 가중치를 전부 지웁니다. 그 달은 요일 기본값만으로 계산됩니다.`, { danger: true });
    if (!ok) return;
    run(async () => {
      const r = await centerPerfAPI.clearWeights(month);
      show(r.data || null);
      return r;
    }, (d) => `${month} 가중치 ${d.removed || 0}행을 지웠습니다`);
  };

  const saveDow = () => {
    if (!dowChanged || busy) return;
    const values = dowDraft.map((v) => v.trim());
    run(async () => {
      const r = await centerPerfAPI.saveDowDefaults(values);
      const again = await centerPerfAPI.weights(month);           // 기본값이 바뀌면 하루 가중치·누적 진척도도 바뀐다
      show(again.data || null);
      return r;
    }, '요일 기본값을 저장했습니다');
  };

  const lead = days.length ? days[0].weekday : 0;
  const storedDays = days.filter((d) => d.stored).length;

  return (
    <div style={card}>
      <div style={hint}>
        하루 가중치 = 요일지수(비면 요일 기본값) × 주차지수 × 휴일고려 × 휴일계수 × 기타계수(빈 칸 = 1). 누적 진척도 = 1일~그날 가중치 합 ÷ 그 달 전체 합.
        BPO 가져오기의 요일지수(연도) 시트로 채우거나 여기서 날짜별로 고칩니다.
        {canManage ? '' : ' 이 화면은 읽기 전용입니다.'}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        <input type="month" aria-label="가중치 월" value={month} style={inp} onChange={(e) => setMonth(e.target.value)} />
        {data && (
          <span data-testid="perf-weights-summary" style={{ fontSize: '12px', color: COLORS.textSub }}>
            저장된 날 {storedDays}일 · 기본값 {data.defaulted_days ?? 0}일 · 이 달 가중치 합 {data.progress_total ?? '—'}
          </span>
        )}
      </div>
      {loadError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{loadError}</div>}
      {data?.missing && (
        <div role="status" data-testid="perf-weights-missing"
          style={{ ...badge('warn'), display: 'block', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', marginBottom: '8px' }}>
          {MISSING_TEXT}
        </div>
      )}
      {msg && (
        <div role={msg.tone === 'err' ? 'alert' : 'status'} data-testid="perf-weights-msg"
          style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: msg.tone === 'err' ? COLORS.dangerText : COLORS.successText }}>
          {msg.text}
        </div>
      )}

      {data && (
        <div data-testid="perf-weights-calendar" style={{ overflowX: 'auto' }}>
          <div style={grid}>
            {DOW.map((d) => (
              <div key={d} style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textSub, textAlign: 'center' }}>{d}</div>
            ))}
            {Array.from({ length: lead }, (_, i) => <div key={`blank-${i}`} data-blank="true" />)}
            {days.map((day) => {
              const active = selected === day.date;
              const dirty = changes.some((c) => c.date === day.date);
              return (
                <button key={day.date} type="button" data-date={day.date} data-weight={day.weight ?? ''}
                  data-cum={day.cum_progress ?? ''} data-stored={day.stored ? 'true' : 'false'}
                  aria-label={`${day.date} 가중치`} aria-pressed={active} onClick={() => setSelected(day.date)}
                  style={{
                    textAlign: 'left', padding: '5px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                    background: active ? COLORS.primarySoft : COLORS.surface,
                    border: `1px solid ${dirty ? COLORS.warn : active ? COLORS.primary : COLORS.borderSoft}`,
                    color: day.weekday >= 5 ? COLORS.dangerText : COLORS.text,
                  }}>
                  <div style={{ fontWeight: 700 }}>{Number(day.date.slice(8, 10))}</div>
                  <div data-part="weight">가중치 {day.weight ?? '—'}</div>
                  <div data-part="cum">누적 {pctText(day.cum_progress)}</div>
                  <div data-part="source" style={{ color: day.stored ? COLORS.successText : COLORS.textMuted }}>
                    {dirty ? '수정 중' : day.stored ? '저장값' : '기본값'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedDay && (
        <div data-testid="perf-weights-editor" style={{ marginTop: '10px', padding: '10px', border: `1px solid ${COLORS.borderSoft}`, borderRadius: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
            {selectedDay.date} ({DOW[selectedDay.weekday]}) · 하루 가중치 {selectedDay.weight ?? '—'} · 누적 진척도 {pctText(selectedDay.cum_progress)}
            {!selectedDay.stored && <span style={{ ...badge('warn'), marginLeft: '6px' }}>저장값 없음(기본값)</span>}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {FACTORS.map(([k, label]) => (
              <span key={k} data-col={k} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', fontSize: '12px' }}>
                {label}
                {canManage ? (
                  <input aria-label={`${selectedDay.date} ${label}`} inputMode="decimal" value={selectedDraft[k]}
                    placeholder={k === 'dow_index' ? `기본 ${dowDefaults[selectedDay.weekday] ?? ''}` : '1'}
                    style={{ ...inp, width: '80px', textAlign: 'right' }}
                    onChange={(e) => setDraft(selectedDay, k, e.target.value)} />
                ) : (
                  <b>{selectedDay[k] ?? '—'}</b>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {canManage && data && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
          <button type="button" disabled={busy || !changes.length} onClick={save}
            style={{ ...btn(COLORS.primary), opacity: busy || !changes.length ? 0.5 : 1 }}>가중치 저장</button>
          <button type="button" disabled={busy || !storedDays} onClick={clear}
            style={{ ...btn(COLORS.dangerSoft, COLORS.dangerText), opacity: busy || !storedDays ? 0.5 : 1 }}>이 달 가중치 비우기</button>
          {changes.length > 0 && <span style={{ fontSize: '12px', color: COLORS.warn, alignSelf: 'center' }}>저장하지 않은 날 {changes.length}일</span>}
        </div>
      )}

      {data && (
        <div data-testid="perf-weights-dow" style={{ marginTop: '12px', fontSize: '12px' }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>요일 기본값(요일지수가 빈 날에 씁니다)</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {DOW.map((d, i) => (
              <span key={d} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                {d}
                {canManage ? (
                  <input aria-label={`${d} 기본 요일지수`} inputMode="decimal" value={dowDraft[i] ?? ''} style={{ ...inp, width: '64px', textAlign: 'right' }}
                    onChange={(e) => setDowDraft((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))} />
                ) : (
                  <b data-dow={d}>{dowDefaults[i] ?? '—'}</b>
                )}
              </span>
            ))}
            {canManage && (
              <button type="button" disabled={busy || !dowChanged} onClick={saveDow}
                style={{ ...btn(COLORS.primary), opacity: busy || !dowChanged ? 0.5 : 1 }}>요일 기본값 저장</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
